/**
 * Cookie utility functions for managing application state
 */

interface CookieOptions {
  expires?: number; // days
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: "Strict" | "Lax" | "None";
}

/**
 * Set a cookie with the given name, value, and options
 */
const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  const {
    expires = 30, // default to 30 days
    path = "/",
    domain,
    secure = false,
    sameSite = "Lax",
  } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (expires) {
    const date = new Date();
    date.setTime(date.getTime() + expires * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${date.toUTCString()}`;
  }

  if (path) {
    cookieString += `; path=${path}`;
  }

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  if (secure) {
    cookieString += "; secure";
  }

  cookieString += `; samesite=${sameSite}`;

  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 */
const getCookie = (name: string): string | null => {
  const nameEQ = encodeURIComponent(name) + "=";
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === " ") {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }
  return null;
};

/**
 * Delete a cookie by name
 */
const deleteCookie = (name: string, path: string = "/"): void => {
  document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
};

/**
 * Postcode cookie management functions
 */

interface PostcodeCookieData {
  postcode: string;
  suburb: string;
  state: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  distance: number;
  discountAmount: number;
  timestamp: string;
}

/**
 * Set postcode information in cookies with 12 months expiry
 */
export const setPostcodeCookie = (postcodeData: PostcodeCookieData): void => {
  const cookieData = {
    ...postcodeData,
    timestamp: new Date().toISOString(),
  };

  setCookie("visitor_postcode", JSON.stringify(cookieData), {
    expires: 365, // 12 months (365 days)
    path: "/",
  });
};

/**
 * Get postcode information from cookies
 */
export const getPostcodeCookie = (): PostcodeCookieData | null => {
  const cookieValue = getCookie("visitor_postcode");
  if (!cookieValue) return null;

  try {
    return JSON.parse(cookieValue);
  } catch (error) {
    console.error("Error parsing postcode cookie:", error);
    return null;
  }
};

/**
 * Check if postcode cookie exists and is valid (not older than 12 months)
 */
export const hasValidPostcodeCookie = (): boolean => {
  const postcodeData = getPostcodeCookie();
  if (!postcodeData || !postcodeData.postcode) return false;

  // Check if timestamp exists and is valid
  if (!postcodeData.timestamp) return false;

  try {
    // Check if cookie is not older than 12 months (365 days)
    const cookieDate = new Date(postcodeData.timestamp);

    // Check if the date is valid
    if (isNaN(cookieDate.getTime())) return false;

    const now = new Date();
    const daysDiff = (now.getTime() - cookieDate.getTime()) / (1000 * 3600 * 24);

    return daysDiff <= 365 && daysDiff >= 0; // Ensure it's not a future date
  } catch (error) {
    console.error("Error validating postcode cookie timestamp:", error);
    return false;
  }
};

/**
 * Clear postcode cookie
 */
export const clearPostcodeCookie = (): void => {
  deleteCookie("visitor_postcode");
};
